/**
 * Script Provider Registry
 *
 * Central registry for database engine script providers.
 * Use getScriptProvider(engine) to get the appropriate provider.
 */

import type { DatabaseEngineId, UserFacingEngineId } from '@/core/engines';
import type { ScriptProvider } from '../types/scriptProvider';

const providers = new Map<UserFacingEngineId, ScriptProvider>();

/**
 * Register a script provider for a database engine
 * @param provider - The script provider to register
 */
export function registerScriptProvider(provider: ScriptProvider): void {
  providers.set(provider.engine, provider);
}

/**
 * Get the script provider for a database engine
 * @param engine - The database engine (defaults to 'mssql')
 * @returns The registered script provider
 * @throws Error if no provider is registered for the engine
 */
export function getScriptProvider(engine: DatabaseEngineId | string = 'mssql'): ScriptProvider {
  // Normalize engine - treat 'mock' as 'mssql' for scripting (uses same SQL syntax)
  const rawEngine = engine || 'mssql';
  const normalizedEngine: UserFacingEngineId = rawEngine === 'mock' ? 'mssql' : (rawEngine as UserFacingEngineId);

  const provider = providers.get(normalizedEngine);

  if (!provider) {
    throw new Error(`No script provider registered for engine: ${engine}`);
  }

  return provider;
}

/**
 * Check if a script provider is registered for an engine
 * @param engine - The database engine
 * @returns True if a provider is registered
 */
export function hasScriptProvider(engine: DatabaseEngineId | string): boolean {
  // Normalize engine - treat 'mock' as 'mssql'
  const normalizedEngine: UserFacingEngineId = engine === 'mock' ? 'mssql' : (engine as UserFacingEngineId);
  return providers.has(normalizedEngine);
}

/**
 * Get all registered engine types
 * @returns Array of registered database engines
 */
export function getRegisteredEngines(): UserFacingEngineId[] {
  return Array.from(providers.keys());
}
